import { SEARCH, URLS, FONTS, DISPLAY_NAME } from '../../config/app-config';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import {
    ENTERPRISE_SEARCH_PAGE_SIZE,
    fetchEnterpriseFacets,
    searchEnterprise,
    suggestEnterprise,
} from '../../services/enterpriseSearchApi';

const DEFAULT_ENTERPRISE_FILTERS = {
    source: 'zenon_docs',
    source_type: 'doc_page',
};

function findAvailableFacetValue(facets, fieldName, requestedValue) {
    if (!requestedValue) return null;
    const facet = facets.find((item) => item.field_name === fieldName);
    const option = facet?.values?.find((item) => (
        item.value === requestedValue || item.alias_for === requestedValue
    ));
    return option?.value || null;
}

export function buildEnterpriseDefaultFilters(facets, release) {
    const desiredFilters = {
        ...DEFAULT_ENTERPRISE_FILTERS,
        language: release.language,
        zenon_version: release.version,
    };

    return Object.entries(desiredFilters).reduce((filters, [fieldName, requestedValue]) => {
        const availableValue = findAvailableFacetValue(facets, fieldName, requestedValue);
        if (availableValue) filters[fieldName] = [availableValue];
        return filters;
    }, {});
}

export async function getEnterpriseSuggestions({ query, release }, signal) {
    const data = await suggestEnterprise(query, release.language, signal);
    return Array.isArray(data?.suggestions) ? data.suggestions : [];
}

function EnterpriseResult({ result }) {
    const keywords = result.keywords?.length ? result.keywords : result.tags || [];
    const summary = result.summary || result.chunk_md || result.content_full_md;

    // Remove single attribute from results
    if (result.source === 'zenon_docs') {
        result.url = String(result.url)
            .replace('&single=true', '')
            .replace(/^[^?]*/, URLS.frontendBase);
    }

    return (
        <Paper
            component={result.url ? 'a' : 'div'}
            href={result.url || undefined}
            target={result.url ? '_self' : undefined}
            rel={result.url ? 'noopener noreferrer' : undefined}
            elevation={0}
            sx={{
                display: 'block',
                p: 2,
                color: 'inherit',
                textDecoration: 'none',
                bgcolor: 'background.default',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                '&:hover': { boxShadow: 3 },
            }}
        >
            <Box display="flex" justifyContent="space-between" gap={2}>
                <Box minWidth={0}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip label={result.source || 'Enterprise Search'} size="small" />
                        {result.source_type && (
                            <Typography variant="caption" color="text.secondary">
                                {result.source_type.replaceAll('_', ' ')}
                            </Typography>
                        )}
                    </Stack>
                    <Typography
                        variant="h6"
                        sx={{ mt: 1, color: 'accent.main', fontFamily: FONTS.heading }}
                    >
                        {result.title || 'Untitled'}
                    </Typography>
                    {result.url && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {result.url}
                        </Typography>
                    )}
                </Box>
                {result.url && <OpenInNewIcon fontSize="small" />}
            </Box>

            {summary && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, whiteSpace: 'pre-line' }}>
                    {summary.length > 350 ? `${summary.slice(0, 350)}…` : summary}
                </Typography>
            )}

            {keywords.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                    {keywords.slice(0, 6).map((keyword) => (
                        <Chip key={keyword} label={keyword} size="small" variant="outlined" />
                    ))}
                </Stack>
            )}
        </Paper>
    );
}

const EnterpriseSearch = ({
    request,
    submittedQuery,
    selectedRelease,
}) => {
    const [facets, setFacets] = useState([]);
    const [facetsLoaded, setFacetsLoaded] = useState(false);
    const [filters, setFilters] = useState({});
    const [response, setResponse] = useState(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const filtersRef = useRef({});
    const searchController = useRef(null);
    const searchSequence = useRef(0);
    const lastRequestId = useRef(null);
    const defaultsContextKey = useRef('');

    useEffect(() => {
        const controller = new AbortController();
        fetchEnterpriseFacets(controller.signal)
            .then((data) => {
                setFacets(Array.isArray(data?.facets) ? data.facets : []);
                setFacetsLoaded(true);
            })
            .catch((fetchError) => {
                if (fetchError?.name === 'AbortError') return;
                console.error('Enterprise search facets failed:', fetchError);
                setFacetsLoaded(true);
            });
        return () => controller.abort();
    }, []);

    const performSearch = useCallback((query, nextPage = 1, nextFilters = filtersRef.current) => {
        if (!query) return;
        searchController.current?.abort();
        const controller = new AbortController();
        searchController.current = controller;
        const sequence = ++searchSequence.current;

        setPage(nextPage);
        setLoading(true);
        setError('');
        searchEnterprise({
            query,
            filters: nextFilters,
            size: ENTERPRISE_SEARCH_PAGE_SIZE,
            from: (nextPage - 1) * ENTERPRISE_SEARCH_PAGE_SIZE,
            userLanguage: selectedRelease.language,
        }, controller.signal)
            .then((data) => {
                if (sequence !== searchSequence.current) return;
                setResponse({
                    ...data,
                    results: Array.isArray(data?.results) ? data.results : [],
                    total: Number(data?.total) || 0,
                });
            })
            .catch((searchError) => {
                if (searchError?.name === 'AbortError' || sequence !== searchSequence.current) return;
                console.error('Enterprise search failed:', searchError);
                setResponse(null);
                setError(searchError.message || 'Enterprise search is currently unavailable.');
            })
            .finally(() => {
                if (sequence === searchSequence.current) setLoading(false);
            });
    }, [selectedRelease]);

    useEffect(() => () => searchController.current?.abort(), []);

    useEffect(() => {
        if (!facetsLoaded) return;
        const contextKey = JSON.stringify([selectedRelease, facets]);
        if (defaultsContextKey.current === contextKey) return;
        defaultsContextKey.current = contextKey;

        const defaults = buildEnterpriseDefaultFilters(facets, selectedRelease);
        const nextFilters = { ...filtersRef.current, ...defaults };
        ['language', 'zenon_version', 'source', 'source_type'].forEach((fieldName) => {
            if (!defaults[fieldName]) delete nextFilters[fieldName];
        });

        filtersRef.current = nextFilters;
        setFilters(nextFilters);
        if (request?.query) {
            lastRequestId.current = request.id;
            performSearch(request.query, 1, nextFilters);
        }
    }, [facets, facetsLoaded, performSearch, request?.id, request?.query, selectedRelease]);

    useEffect(() => {
        if (!facetsLoaded || !request?.query || lastRequestId.current === request.id) return;
        lastRequestId.current = request.id;
        performSearch(request.query, 1, filtersRef.current);
    }, [facetsLoaded, performSearch, request]);

    const handleFilterChange = useCallback((fieldName, value) => {
        const values = Array.isArray(value) ? value : String(value).split(',');
        const nextFilters = { ...filtersRef.current };
        if (values.length) nextFilters[fieldName] = values;
        else delete nextFilters[fieldName];
        filtersRef.current = nextFilters;
        setFilters(nextFilters);
        if (submittedQuery) performSearch(submittedQuery, 1, nextFilters);
    }, [performSearch, submittedQuery]);

    const clearFilters = useCallback(() => {
        filtersRef.current = {};
        setFilters({});
        if (submittedQuery) performSearch(submittedQuery, 1, {});
    }, [performSearch, submittedQuery]);

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil((response?.total || 0) / ENTERPRISE_SEARCH_PAGE_SIZE)),
        [response?.total]
    );

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 1200,
                height: '100%',
                minHeight: 0,
                mx: 'auto',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '240px minmax(0, 1fr)' },
                gridTemplateRows: { xs: 'auto minmax(0, 1fr)', md: 'minmax(0, 1fr)' },
                gap: 3,
            }}
        >
            <Stack spacing={2} sx={{ minHeight: 0, overflow: 'hidden' }}>
                <Typography variant="subtitle1" fontWeight={700}>Filters</Typography>
                {facets.map((facet) => (
                    <FormControl key={facet.field_name} size="small" fullWidth>
                        <InputLabel>{facet.display_name}</InputLabel>
                        <Select
                            multiple
                            label={facet.display_name}
                            value={filters[facet.field_name] || []}
                            onChange={(event) => handleFilterChange(facet.field_name, event.target.value)}
                            renderValue={(selected) => selected.map((value) => (
                                facet.values?.find((option) => option.value === value)?.display_name || value
                            )).join(', ')}
                        >
                            {(facet.values || []).map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    <Checkbox checked={(filters[facet.field_name] || []).includes(option.value)} />
                                    <ListItemText
                                        primary={option.display_name}
                                        secondary={option.document_count?.toLocaleString()}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ))}
                {Object.keys(filters).length > 0 && (
                    <Button onClick={clearFilters} sx={{
                        color: 'accent.main',
                        borderColor: 'accent.main'
                    }}>
                        Clear filters
                    </Button>
                )}
                {SEARCH.frontendBaseUrl && (
                    <Button
                        component="a"
                        sx={{
                            color: 'accent.main',
                            borderColor: 'accent.main'
                        }}
                        href={`${SEARCH.frontendBaseUrl}/?q=${encodeURIComponent(submittedQuery)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        endIcon={<OpenInNewIcon />}
                    >
                        Open full Enterprise Search
                    </Button>
                )}
            </Stack>

            <Box sx={{ minWidth: 0, minHeight: 0, overflowY: 'auto', pb: 2, pt: 1 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {response?.acl_status === 'limited' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Sign in to include resources that require enterprise access.
                    </Alert>
                )}

                {loading ? (
                    <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>
                ) : response?.results?.length ? (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {response.total.toLocaleString()} results for “{submittedQuery}”
                            {response.took_ms ? ` (${response.took_ms} ms)` : ''}
                        </Typography>
                        <Stack spacing={2}>
                            {response.results.map((result) => (
                                <EnterpriseResult key={result.id} result={result} />
                            ))}
                        </Stack>
                        {totalPages > 1 && (
                            <Box display="flex" justifyContent="center" mt={3}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(event, value) => performSearch(submittedQuery, value, filtersRef.current)}
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                />
                            </Box>
                        )}
                    </>
                ) : submittedQuery && response ? (
                    <Alert severity="info">No results found for “{submittedQuery}”.</Alert>
                ) : (
                    <Typography color="text.secondary">
                        Search {DISPLAY_NAME} documentation.
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default EnterpriseSearch;
